/**
 * FikoImporterPointCloud - Point Cloud & Mesh Dosya İmporter
 *
 * Desteklenen formatlar:
 * - PLY (Stanford Polygon File Format)
 * - XYZ (Simple ASCII point format)
 * - PTS (Point cloud format)
 * - ASC (ASCII point format)
 * - PCD (Point Cloud Data - PCL format)
 * - OBJ (Wavefront OBJ - mesh & point cloud)
 * - STL (Stereolithography - ASCII & Binary)
 */

import * as THREE from 'three';
import { FikoSahne } from './FikoSahne';
import { FikoGrup } from './FikoGrup';
import { fikoObjeyiStepIndir, fikoStepAyarlariniDuzenle } from './FikoThreeCam/FikoSTEPDisaAktar';

/** Desteklenen point cloud formatları */
export const POINT_CLOUD_FORMATS = ['.ply', '.xyz', '.pts', '.asc', '.pcd', '.obj', '.stl'];

/** Point cloud verisi */
export interface PointCloudData {
    positions: Float32Array;
    colors: Float32Array;
    normals: Float32Array | null;
    pointCount: number;
    /** Dosyadan okunan face indeksleri (varsa) */
    indices?: Uint32Array;
    /** Face sayısı */
    faceCount?: number;
}

/**
 * Point Cloud Grup - Ürün ağacında görünür
 */
export class FikoPointCloudGrup extends FikoGrup {
    public override readonly type = 'FikoPointCloud';

    public pointCloudData: PointCloudData | null = null;

    constructor(sahne: FikoSahne, name: string = 'Point Cloud') {
        super(sahne, name);

        this.userData['FikoArayuz'] = {
            simge: '/simgeler/kamera.webp',
            baslik: name,
            renk: '#00E5FF',
            agactaGorunur: true
        };

        // Sağ tık menüsü
        this.sagMenu = [
            {
                baslik: 'Ekrana Sığdır',
                simge: 'expand',
                tiklandi: () => {
                    this.fitToScreen();
                }
            },
            {
                baslik: 'Göster/Gizle',
                simge: 'eye',
                tiklandi: () => {
                    this.visible = !this.visible;
                    this.sahne?.anaGosterim?.ciz();
                }
            },
            {
                baslik: 'Mesh Oluştur',
                simge: 'cube',
                tiklandi: () => {
                    this.reconstructMesh();
                }
            },
            {
                baslik: 'Nokta/Mesh Değiştir',
                simge: 'exchange',
                tiklandi: () => {
                    this.togglePointsMesh();
                }
            },
            {
                baslik: 'Export PLY',
                simge: 'download',
                tiklandi: () => {
                    this.exportPLY();
                }
            },
            {
                baslik: 'STEP olarak indir (B-Rep)',
                simge: 'download',
                tiklandi: async () => {
                    try { await fikoObjeyiStepIndir(this, 'brep', this.name); }
                    catch (e: any) {
                        console.error('STEP (B-Rep) disa aktarim hatasi', e);
                        alert('STEP olarak aktarilamadi: ' + (e?.message || e));
                    }
                }
            },
            {
                baslik: 'STEP olarak indir (AP242 Tessellated)',
                simge: 'download',
                tiklandi: async () => {
                    try { await fikoObjeyiStepIndir(this, 'tessellated', this.name); }
                    catch (e: any) {
                        console.error('STEP (Tessellated) disa aktarim hatasi', e);
                        alert('STEP olarak aktarilamadi: ' + (e?.message || e));
                    }
                }
            },
            {
                baslik: 'STEP ayarları...',
                simge: 'cog',
                tiklandi: () => { fikoStepAyarlariniDuzenle(); }
            },
            {
                baslik: 'Sil',
                simge: 'trash',
                renk: '#ff6b6b',
                tiklandi: () => {
                    this.parent?.remove(this);
                    this.sahne?.anaGosterim?.ciz();
                }
            }
        ];
    }

    /**
     * Modeli ekrana sığdır (animasyonlu kamera zoom)
     */
    fitToScreen(): void {
        const anaGosterim = this.sahne?.anaGosterim;
        if (!anaGosterim) {
            console.error('[PointCloud] anaGosterim bulunamadı');
            return;
        }

        // Mesh veya Points bul
        const mesh = this.children.find(c => c instanceof THREE.Mesh) as THREE.Mesh;
        const points = this.children.find(c => c instanceof THREE.Points) as THREE.Points;
        const targetObj = mesh || points;

        if (!targetObj || !targetObj.geometry) {
            console.error('[PointCloud] Geometry bulunamadı');
            return;
        }

        // BoundingSphere hesapla
        targetObj.geometry.computeBoundingSphere();
        const boundingSphere = targetObj.geometry.boundingSphere;

        if (!boundingSphere || boundingSphere.radius === 0) {
            console.error('[PointCloud] Geçersiz bounding sphere');
            return;
        }

        // World matrix uygula
        targetObj.updateMatrixWorld(true);
        const center = boundingSphere.center.clone().applyMatrix4(targetObj.matrixWorld);
        const radius = boundingSphere.radius;

        // Canvas boyutları
        const canvas = anaGosterim.canvas?.nativeElement;
        if (!canvas || canvas.clientWidth === 0 || canvas.clientHeight === 0) {
            console.error('[PointCloud] Canvas bulunamadı veya boyutu 0');
            return;
        }

        // Zoom hesapla
        const padding = 1.2; // %20 padding
        const aspect = canvas.clientWidth / canvas.clientHeight;
        const paddedRadius = radius * padding;
        const hedefZoom = (aspect > 1.0)
            ? canvas.clientHeight / (paddedRadius * 2)
            : canvas.clientWidth / (paddedRadius * 2);

        // Kamera ve kontrol
        const kamera = anaGosterim.aktifKamera;
        const kontrol = anaGosterim.aktifKontrol;

        if (!kamera || !kontrol) {
            console.error('[PointCloud] Kamera veya kontrol bulunamadı');
            return;
        }

        // Hedef pozisyon hesapla
        const eskiTarget = kontrol.target.clone();
        const offset = center.clone().sub(eskiTarget);
        const hedefKameraPoz = kamera.position.clone().add(offset);

        // TWEEN animasyonu
        const TWEEN = (window as any).TWEEN;
        if (typeof TWEEN === 'undefined') {
            // TWEEN yoksa direkt uygula
            kamera.position.copy(hedefKameraPoz);
            kontrol.target.copy(center);
            kamera.zoom = hedefZoom;
            kamera.updateProjectionMatrix();
            kontrol.update();
            anaGosterim.ciz();
            return;
        }

        // Sürekli çizim başlat
        anaGosterim.surekliCiz?.();

        const animSuresi = 500; // ms

        // Kamera pozisyon ve zoom animasyonu
        new TWEEN.Tween({
            x: kamera.position.x,
            y: kamera.position.y,
            z: kamera.position.z,
            zoom: kamera.zoom
        })
        .to({
            x: hedefKameraPoz.x,
            y: hedefKameraPoz.y,
            z: hedefKameraPoz.z,
            zoom: hedefZoom
        }, animSuresi)
        .easing(TWEEN.Easing.Quadratic.Out)
        .onUpdate((val: any) => {
            kamera.position.set(val.x, val.y, val.z);
            kamera.zoom = val.zoom;
            kamera.updateProjectionMatrix();
        })
        .onComplete(() => {})
        .start();

        // Target animasyonu
        new TWEEN.Tween({
            x: kontrol.target.x,
            y: kontrol.target.y,
            z: kontrol.target.z
        })
        .to({
            x: center.x,
            y: center.y,
            z: center.z
        }, animSuresi)
        .easing(TWEEN.Easing.Quadratic.Out)
        .onUpdate((val: any) => {
            kontrol.target.set(val.x, val.y, val.z);
            kontrol.update();
        })
        .start();
    }

    /** Mesh var mı? */
    private hasMesh: boolean = false;

    /**
     * Point cloud'dan mesh oluştur (Surface Reconstruction)
     * Delaunay-benzeri triangulation kullanır
     */
    reconstructMesh(): void {
        if (!this.pointCloudData || this.pointCloudData.pointCount < 4) {
            console.warn('[PointCloud] Mesh oluşturmak için yeterli nokta yok');
            return;
        }

        // Dosyadan yüklenen mesh varsa uyar
        if (this.pointCloudData.indices && this.pointCloudData.faceCount && this.pointCloudData.faceCount > 0) {
            const existingMesh = this.children.find(c => c.name === '__reconstructed_mesh__');
            if (existingMesh) {
                // console.log('[PointCloud] Dosyada zaten mesh bilgisi var. Mevcut mesh kullanılıyor.');
                return;
            }
        }

        // Önceki mesh varsa kaldır
        const oldMesh = this.children.find(c => c.name === '__reconstructed_mesh__');
        if (oldMesh) {
            this.remove(oldMesh);
            (oldMesh as THREE.Mesh).geometry.dispose();
            ((oldMesh as THREE.Mesh).material as THREE.Material).dispose();
        }

        // console.log('[PointCloud] Mesh reconstruction başlıyor...');
        const startTime = performance.now();

        const positions = this.pointCloudData.positions;
        const colors = this.pointCloudData.colors;
        const pointCount = this.pointCloudData.pointCount;

        // Bounding box hesapla
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

        for (let i = 0; i < pointCount; i++) {
            const x = positions[i * 3];
            const y = positions[i * 3 + 1];
            const z = positions[i * 3 + 2];
            minX = Math.min(minX, x); maxX = Math.max(maxX, x);
            minY = Math.min(minY, y); maxY = Math.max(maxY, y);
            minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
        }

        const diagonal = Math.sqrt(
            Math.pow(maxX - minX, 2) +
            Math.pow(maxY - minY, 2) +
            Math.pow(maxZ - minZ, 2)
        );

        // Noktaları Vector3 array'ine dönüştür
        const points: THREE.Vector3[] = [];
        for (let i = 0; i < pointCount; i++) {
            points.push(new THREE.Vector3(
                positions[i * 3],
                positions[i * 3 + 1],
                positions[i * 3 + 2]
            ));
        }

        // console.log(`[PointCloud] ${pointCount} nokta, diagonal: ${diagonal.toFixed(2)}`);

        // Maksimum kenar uzunluğu - diagonal'in %5'i veya ortalama mesafenin 5 katı
        const avgDist = this.calculateAverageDistance(points);
        const maxDistance = Math.max(avgDist * 8, diagonal * 0.08);
        // console.log(`[PointCloud] avgDist: ${avgDist.toFixed(4)}, maxDistance: ${maxDistance.toFixed(4)}`);

        // K-Nearest Neighbors tabanlı triangulation
        const k = Math.min(20, Math.max(8, Math.floor(pointCount / 50)));

        // Üçgenleri oluştur
        const indices: number[] = [];
        const usedTriangles = new Set<string>();

        for (let i = 0; i < pointCount; i++) {
            const neighbors = this.findKNearestNeighbors(points, i, k, maxDistance);

            if (neighbors.length < 2) continue;

            // Komşular arasında üçgenler oluştur
            for (let j = 0; j < neighbors.length; j++) {
                for (let m = j + 1; m < neighbors.length; m++) {
                    const n1 = neighbors[j];
                    const n2 = neighbors[m];

                    // Üçgen zaten var mı kontrol et
                    const sorted = [i, n1, n2].sort((a, b) => a - b);
                    const triKey = `${sorted[0]}-${sorted[1]}-${sorted[2]}`;
                    if (usedTriangles.has(triKey)) continue;

                    // Üçgen kalitesini kontrol et
                    if (this.isValidTriangle(points, i, n1, n2, maxDistance)) {
                        indices.push(i, n1, n2);
                        usedTriangles.add(triKey);
                    }
                }
            }
        }

        // console.log(`[PointCloud] ${indices.length / 3} üçgen bulundu`);

        if (indices.length === 0) {
            console.warn('[PointCloud] Mesh oluşturulamadı - alternatif yöntem deneniyor...');
            // Alternatif: ConvexHull benzeri basit triangulation
            this.createSimpleMesh(points, positions, colors);
            return;
        }

        // Mesh geometry oluştur
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();

        // Mesh material
        const material = new THREE.MeshPhongMaterial({
            vertexColors: true,
            side: THREE.DoubleSide,
            flatShading: true,
            shininess: 30
        });

        // Mesh oluştur
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = '__reconstructed_mesh__';

        // Gruba ekle
        this.add(mesh);
        this.hasMesh = true;

        // Point cloud'u gizle
        const pointCloud = this.children.find(c => c.name === '__point_cloud__');
        if (pointCloud) {
            pointCloud.visible = false;
        }

        const elapsed = performance.now() - startTime;
        // console.log(`[PointCloud] ✓ Mesh oluşturuldu: ${indices.length / 3} üçgen, ${elapsed.toFixed(0)}ms`);

        this.sahne?.anaGosterim?.ciz();
    }

    /**
     * Basit mesh oluştur (alternatif yöntem)
     * Noktaları gruplara ayırıp her grupta triangulation yapar
     */
    private createSimpleMesh(
        points: THREE.Vector3[],
        positions: Float32Array,
        colors: Float32Array
    ): void {
        // console.log('[PointCloud] Basit mesh oluşturma yöntemi...');

        const indices: number[] = [];
        const pointCount = points.length;

        // Her nokta için en yakın 2 komşuyu bul ve üçgen oluştur
        for (let i = 0; i < pointCount; i++) {
            const distances: { idx: number; dist: number }[] = [];

            for (let j = 0; j < pointCount; j++) {
                if (i === j) continue;
                distances.push({
                    idx: j,
                    dist: points[i].distanceTo(points[j])
                });
            }

            distances.sort((a, b) => a.dist - b.dist);

            // En yakın 2 komşu ile üçgen oluştur
            if (distances.length >= 2) {
                const n1 = distances[0].idx;
                const n2 = distances[1].idx;

                // Sadece i < n1 < n2 durumunda ekle (tekrar önleme)
                if (i < n1 && n1 < n2) {
                    indices.push(i, n1, n2);
                }
            }
        }

        if (indices.length === 0) {
            console.warn('[PointCloud] Hiçbir üçgen oluşturulamadı');
            return;
        }

        // Geometry oluştur
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();

        // Material
        const material = new THREE.MeshPhongMaterial({
            vertexColors: true,
            side: THREE.DoubleSide,
            flatShading: true,
            shininess: 30
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = '__reconstructed_mesh__';
        this.add(mesh);
        this.hasMesh = true;

        // Point cloud'u gizle
        const pointCloud = this.children.find(c => c.name === '__point_cloud__');
        if (pointCloud) {
            pointCloud.visible = false;
        }

        // console.log(`[PointCloud] ✓ Basit mesh oluşturuldu: ${indices.length / 3} üçgen`);
        this.sahne?.anaGosterim?.ciz();
    }

    /**
     * Ortalama nokta mesafesini hesapla
     */
    private calculateAverageDistance(points: THREE.Vector3[]): number {
        if (points.length < 2) return 1;

        let totalDist = 0;
        let count = 0;
        const sampleSize = Math.min(200, points.length);

        for (let i = 0; i < sampleSize; i++) {
            const idx = Math.floor(Math.random() * points.length);
            let minDist = Infinity;

            // En yakın 5 komşuyu bul ve ortalama al
            const distances: number[] = [];
            for (let j = 0; j < points.length; j++) {
                if (idx === j) continue;
                distances.push(points[idx].distanceTo(points[j]));
            }
            distances.sort((a, b) => a - b);

            if (distances.length > 0) {
                // En yakın 3 komşunun ortalaması
                const nearestCount = Math.min(3, distances.length);
                for (let k = 0; k < nearestCount; k++) {
                    totalDist += distances[k];
                    count++;
                }
            }
        }

        return count > 0 ? totalDist / count : 1;
    }

    /**
     * K en yakın komşuyu bul
     */
    private findKNearestNeighbors(
        points: THREE.Vector3[],
        index: number,
        k: number,
        maxDistance: number
    ): number[] {
        const distances: { idx: number; dist: number }[] = [];
        const point = points[index];

        for (let i = 0; i < points.length; i++) {
            if (i === index) continue;
            const dist = point.distanceTo(points[i]);
            // maxDistance kontrolünü gevşet
            if (dist <= maxDistance * 1.5) {
                distances.push({ idx: i, dist });
            }
        }

        distances.sort((a, b) => a.dist - b.dist);
        return distances.slice(0, k).map(d => d.idx);
    }

    /**
     * Üçgenin geçerli olup olmadığını kontrol et
     */
    private isValidTriangle(
        points: THREE.Vector3[],
        i1: number,
        i2: number,
        i3: number,
        maxEdge: number
    ): boolean {
        const p1 = points[i1];
        const p2 = points[i2];
        const p3 = points[i3];

        // Kenar uzunluklarını kontrol et
        const d1 = p1.distanceTo(p2);
        const d2 = p2.distanceTo(p3);
        const d3 = p3.distanceTo(p1);

        // Maksimum kenar kontrolü - daha esnek
        const maxAllowed = maxEdge * 2;
        if (d1 > maxAllowed || d2 > maxAllowed || d3 > maxAllowed) return false;

        // Minimum kenar kontrolü (dejenere üçgenleri önle)
        const minEdge = Math.min(d1, d2, d3);
        if (minEdge < 0.0001) return false;

        // Üçgen alanını kontrol et
        const edge1 = new THREE.Vector3().subVectors(p2, p1);
        const edge2 = new THREE.Vector3().subVectors(p3, p1);
        const cross = new THREE.Vector3().crossVectors(edge1, edge2);
        const area = cross.length() / 2;

        // Minimum alan kontrolü - çok gevşek
        if (area < 0.0000001) return false;

        // Aspect ratio kontrolü (çok ince üçgenleri reddet)
        const maxD = Math.max(d1, d2, d3);
        const perimeter = d1 + d2 + d3;
        const aspectRatio = maxD / (perimeter / 3);

        // Aspect ratio 3'ten büyükse reddet (çok ince üçgen)
        if (aspectRatio > 4) return false;

        return true;
    }

    /**
     * Points ve Mesh görünümü arasında geçiş yap
     */
    togglePointsMesh(): void {
        let pointCloud = this.children.find(c => c.name === '__point_cloud__') as THREE.Points;
        const mesh = this.children.find(c => c.name === '__reconstructed_mesh__') as THREE.Mesh;

        // Mesh var ama point cloud yoksa, point cloud oluştur
        if (mesh && !pointCloud && this.pointCloudData) {
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.pointCloudData.positions, 3));
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.pointCloudData.colors, 3));
            geometry.computeBoundingBox();
            geometry.computeBoundingSphere();

            const material = new THREE.PointsMaterial({
                size: 2,
                vertexColors: true,
                sizeAttenuation: true
            });

            pointCloud = new THREE.Points(geometry, material);
            pointCloud.name = '__point_cloud__';
            pointCloud.visible = false; // Başlangıçta gizli
            this.add(pointCloud);
            // console.log('[PointCloud] Point cloud görünümü oluşturuldu');
        }

        // Şimdi geçiş yap
        if (pointCloud && mesh) {
            pointCloud.visible = !pointCloud.visible;
            mesh.visible = !mesh.visible;
            // console.log(`[PointCloud] Görünüm: ${mesh.visible ? 'Mesh' : 'Point Cloud'}`);
        } else if (pointCloud && !mesh) {
            // console.log('[PointCloud] Önce "Mesh Oluştur" seçeneğini kullanın');
        }

        this.sahne?.anaGosterim?.ciz();
    }

    /**
     * PLY olarak export et
     */
    exportPLY(): void {
        const pointCloud = this.children.find(c => c instanceof THREE.Points) as THREE.Points;
        if (!pointCloud) {
            console.warn('[PointCloud] Point cloud bulunamadı');
            return;
        }

        const geometry = pointCloud.geometry;
        const positions = geometry.getAttribute('position');
        const colors = geometry.getAttribute('color');

        if (!positions) {
            console.warn('[PointCloud] Position attribute yok');
            return;
        }

        // PLY header
        let ply = 'ply\n';
        ply += 'format ascii 1.0\n';
        ply += `element vertex ${positions.count}\n`;
        ply += 'property float x\n';
        ply += 'property float y\n';
        ply += 'property float z\n';
        if (colors) {
            ply += 'property uchar red\n';
            ply += 'property uchar green\n';
            ply += 'property uchar blue\n';
        }
        ply += 'end_header\n';

        // Vertex data
        for (let i = 0; i < positions.count; i++) {
            const x = positions.getX(i).toFixed(6);
            const y = positions.getY(i).toFixed(6);
            const z = positions.getZ(i).toFixed(6);

            if (colors) {
                const r = Math.floor(colors.getX(i) * 255);
                const g = Math.floor(colors.getY(i) * 255);
                const b = Math.floor(colors.getZ(i) * 255);
                ply += `${x} ${y} ${z} ${r} ${g} ${b}\n`;
            } else {
                ply += `${x} ${y} ${z}\n`;
            }
        }

        // İndir
        const blob = new Blob([ply], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.name.replace(/\s+/g, '_')}_${Date.now()}.ply`;
        a.click();
        URL.revokeObjectURL(url);
    }
}

/**
 * Point Cloud İmporter
 */
export class FikoImporterPointCloud {
    /** Progress callback */
    public onProgress?: (mesaj: string, progress: number) => void;

    constructor(public sahne: FikoSahne) {}

    /**
     * Dosyayı yükle (otomatik format algılama)
     */
    public async yukle(dosya: File): Promise<FikoPointCloudGrup | null> {
        const extension = dosya.name.toLowerCase().substring(dosya.name.lastIndexOf('.'));

        this.onProgress?.(`Point cloud yükleniyor: ${dosya.name}`, 10);

        switch (extension) {
            case '.ply':
                return this.importPLY(dosya);
            case '.xyz':
            case '.pts':
            case '.asc':
                return this.importXYZ(dosya);
            case '.pcd':
                return this.importPCD(dosya);
            case '.obj':
                return this.importOBJ(dosya);
            case '.stl':
                return this.importSTL(dosya);
            default:
                console.error(`[PointCloud] Desteklenmeyen format: ${extension}`);
                return null;
        }
    }

    /**
     * PLY dosyası import et
     * Face bilgisi varsa mesh olarak, yoksa point cloud olarak yükler
     */
    public async importPLY(dosya: File): Promise<FikoPointCloudGrup | null> {
        try {
            this.onProgress?.('PLY dosyası okunuyor...', 20);
            const text = await dosya.text();
            const lines = text.split('\n');

            // Header parse
            let vertexCount = 0;
            let faceCount = 0;
            let hasColor = false;
            let headerEnd = 0;
            let format = 'ascii';

            // Property sırasını takip et
            const vertexProperties: string[] = [];
            let parsingVertexProps = false;

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                if (line.startsWith('format')) {
                    format = line.includes('ascii') ? 'ascii' : 'binary';
                }
                if (line.startsWith('element vertex')) {
                    vertexCount = parseInt(line.split(' ')[2]);
                    parsingVertexProps = true;
                }
                if (line.startsWith('element face')) {
                    faceCount = parseInt(line.split(' ')[2]);
                    parsingVertexProps = false;
                }
                if (line.startsWith('property') && parsingVertexProps) {
                    const parts = line.split(' ');
                    vertexProperties.push(parts[parts.length - 1]);
                }
                if (line.includes('red') || line.includes('diffuse_red')) {
                    hasColor = true;
                }
                if (line === 'end_header') {
                    headerEnd = i + 1;
                    break;
                }
            }

            if (format !== 'ascii') {
                console.warn('[PointCloud] Binary PLY henüz desteklenmiyor');
                return null;
            }

            // console.log(`[PointCloud] PLY: ${vertexCount} vertex, ${faceCount} face`);
            this.onProgress?.(`PLY: ${vertexCount} vertex, ${faceCount} face okunuyor...`, 40);

            // Property indekslerini bul
            const xIdx = vertexProperties.indexOf('x');
            const yIdx = vertexProperties.indexOf('y');
            const zIdx = vertexProperties.indexOf('z');
            let rIdx = vertexProperties.indexOf('red');
            let gIdx = vertexProperties.indexOf('green');
            let bIdx = vertexProperties.indexOf('blue');

            // Diffuse color için alternatif
            if (rIdx === -1) rIdx = vertexProperties.indexOf('diffuse_red');
            if (gIdx === -1) gIdx = vertexProperties.indexOf('diffuse_green');
            if (bIdx === -1) bIdx = vertexProperties.indexOf('diffuse_blue');

            // Vertex data parse
            const positions: number[] = [];
            const colors: number[] = [];

            for (let i = headerEnd; i < headerEnd + vertexCount && i < lines.length; i++) {
                const parts = lines[i].trim().split(/\s+/);
                if (parts.length < 3) continue;

                // Pozisyon
                const x = parseFloat(parts[xIdx >= 0 ? xIdx : 0]);
                const y = parseFloat(parts[yIdx >= 0 ? yIdx : 1]);
                const z = parseFloat(parts[zIdx >= 0 ? zIdx : 2]);

                if (isNaN(x) || isNaN(y) || isNaN(z)) continue;

                positions.push(x, y, z);

                // Renk
                if (hasColor && rIdx >= 0 && gIdx >= 0 && bIdx >= 0) {
                    let r = parseFloat(parts[rIdx]);
                    let g = parseFloat(parts[gIdx]);
                    let b = parseFloat(parts[bIdx]);

                    // 0-255 ise normalize et
                    if (r > 1 || g > 1 || b > 1) {
                        r /= 255;
                        g /= 255;
                        b /= 255;
                    }
                    colors.push(r, g, b);
                } else {
                    colors.push(0.5, 0.7, 1.0); // Varsayılan mavi
                }
            }

            // Face data parse (varsa)
            const indices: number[] = [];
            if (faceCount > 0) {
                const faceStart = headerEnd + vertexCount;
                for (let i = faceStart; i < faceStart + faceCount && i < lines.length; i++) {
                    const parts = lines[i].trim().split(/\s+/);
                    if (parts.length < 4) continue;

                    const vertexCountInFace = parseInt(parts[0]);

                    if (vertexCountInFace === 3) {
                        // Üçgen
                        indices.push(
                            parseInt(parts[1]),
                            parseInt(parts[2]),
                            parseInt(parts[3])
                        );
                    } else if (vertexCountInFace === 4) {
                        // Dörtgen - iki üçgene böl
                        const v0 = parseInt(parts[1]);
                        const v1 = parseInt(parts[2]);
                        const v2 = parseInt(parts[3]);
                        const v3 = parseInt(parts[4]);
                        indices.push(v0, v1, v2);
                        indices.push(v0, v2, v3);
                    } else if (vertexCountInFace > 4) {
                        // Poligon - fan triangulation
                        const v0 = parseInt(parts[1]);
                        for (let j = 2; j < vertexCountInFace; j++) {
                            indices.push(
                                v0,
                                parseInt(parts[j]),
                                parseInt(parts[j + 1])
                            );
                        }
                    }
                }
                // console.log(`[PointCloud] PLY: ${indices.length / 3} üçgen okundu`);
            }

            this.onProgress?.('Model oluşturuluyor...', 80);

            // Face varsa mesh olarak, yoksa point cloud olarak oluştur
            return this.createPointCloudGroup(
                dosya.name,
                positions,
                colors,
                indices.length > 0 ? indices : undefined
            );

        } catch (error: any) {
            console.error(`[PointCloud] PLY import hatası:`, error);
            return null;
        }
    }

    /**
     * XYZ/PTS/ASC dosyası import et
     */
    public async importXYZ(dosya: File): Promise<FikoPointCloudGrup | null> {
        try {
            this.onProgress?.('XYZ dosyası okunuyor...', 20);
            const text = await dosya.text();
            const lines = text.split('\n');

            const positions: number[] = [];
            const colors: number[] = [];

            this.onProgress?.('Noktalar parse ediliyor...', 40);

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;

                const parts = trimmed.split(/[\s,;]+/);
                if (parts.length < 3) continue;

                const x = parseFloat(parts[0]);
                const y = parseFloat(parts[1]);
                const z = parseFloat(parts[2]);

                if (isNaN(x) || isNaN(y) || isNaN(z)) continue;

                positions.push(x, y, z);

                if (parts.length >= 6) {
                    // RGB
                    let r = parseFloat(parts[3]);
                    let g = parseFloat(parts[4]);
                    let b = parseFloat(parts[5]);
                    if (r > 1) r /= 255;
                    if (g > 1) g /= 255;
                    if (b > 1) b /= 255;
                    colors.push(r, g, b);
                } else if (parts.length >= 4) {
                    // Intensity - grayscale
                    let intensity = parseFloat(parts[3]);
                    if (intensity > 1) intensity /= 255;
                    colors.push(intensity, intensity, intensity);
                } else {
                    colors.push(0.5, 0.7, 1.0);
                }
            }

            this.onProgress?.('Point cloud oluşturuluyor...', 80);
            return this.createPointCloudGroup(dosya.name, positions, colors);

        } catch (error: any) {
            console.error(`[PointCloud] XYZ import hatası:`, error);
            return null;
        }
    }

    /**
     * PCD dosyası import et (Point Cloud Data)
     */
    public async importPCD(dosya: File): Promise<FikoPointCloudGrup | null> {
        try {
            this.onProgress?.('PCD dosyası okunuyor...', 20);
            const text = await dosya.text();
            const lines = text.split('\n');

            let pointCount = 0;
            let dataStart = 0;
            let fields: string[] = [];
            let dataType = 'ascii';

            // Header parse
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                if (line.startsWith('FIELDS')) {
                    fields = line.split(' ').slice(1);
                }
                if (line.startsWith('POINTS')) {
                    pointCount = parseInt(line.split(' ')[1]);
                }
                if (line.startsWith('DATA')) {
                    dataType = line.split(' ')[1];
                    dataStart = i + 1;
                    break;
                }
            }

            if (dataType !== 'ascii') {
                console.warn('[PointCloud] Binary PCD henüz desteklenmiyor');
                return null;
            }

            this.onProgress?.(`PCD: ${pointCount} nokta okunuyor...`, 40);

            const xIdx = fields.indexOf('x');
            const yIdx = fields.indexOf('y');
            const zIdx = fields.indexOf('z');
            const rgbIdx = fields.indexOf('rgb');

            const positions: number[] = [];
            const colors: number[] = [];

            for (let i = dataStart; i < dataStart + pointCount && i < lines.length; i++) {
                const parts = lines[i].trim().split(/\s+/);
                if (parts.length < 3) continue;

                const x = parseFloat(parts[xIdx >= 0 ? xIdx : 0]);
                const y = parseFloat(parts[yIdx >= 0 ? yIdx : 1]);
                const z = parseFloat(parts[zIdx >= 0 ? zIdx : 2]);

                if (isNaN(x) || isNaN(y) || isNaN(z)) continue;

                positions.push(x, y, z);

                if (rgbIdx >= 0 && parts[rgbIdx]) {
                    // Packed RGB float
                    const rgbInt = parseInt(parts[rgbIdx]);
                    const r = ((rgbInt >> 16) & 0xFF) / 255;
                    const g = ((rgbInt >> 8) & 0xFF) / 255;
                    const b = (rgbInt & 0xFF) / 255;
                    colors.push(r, g, b);
                } else {
                    colors.push(0.5, 0.7, 1.0);
                }
            }

            this.onProgress?.('Point cloud oluşturuluyor...', 80);
            return this.createPointCloudGroup(dosya.name, positions, colors);

        } catch (error: any) {
            console.error(`[PointCloud] PCD import hatası:`, error);
            return null;
        }
    }

    /**
     * OBJ dosyasından mesh veya point cloud import et
     * Face (f) bilgisi varsa mesh, yoksa point cloud olarak yükler
     */
    public async importOBJ(dosya: File): Promise<FikoPointCloudGrup | null> {
        try {
            this.onProgress?.('OBJ dosyası okunuyor...', 20);
            const text = await dosya.text();
            const lines = text.split('\n');

            const positions: number[] = [];
            const colors: number[] = [];
            const indices: number[] = [];

            this.onProgress?.('OBJ parse ediliyor...', 40);

            for (const line of lines) {
                const trimmed = line.trim();

                // Vertex (v x y z [r g b])
                if (trimmed.startsWith('v ')) {
                    const parts = trimmed.split(/\s+/);
                    if (parts.length < 4) continue;

                    const x = parseFloat(parts[1]);
                    const y = parseFloat(parts[2]);
                    const z = parseFloat(parts[3]);

                    if (isNaN(x) || isNaN(y) || isNaN(z)) continue;

                    positions.push(x, y, z);

                    // OBJ vertex color (non-standard ama bazı programlar destekliyor)
                    if (parts.length >= 7) {
                        colors.push(parseFloat(parts[4]), parseFloat(parts[5]), parseFloat(parts[6]));
                    } else {
                        colors.push(0.5, 0.7, 1.0); // Varsayılan mavi
                    }
                }

                // Face (f v1 v2 v3 ... veya f v1/vt1/vn1 v2/vt2/vn2 ...)
                else if (trimmed.startsWith('f ')) {
                    const parts = trimmed.split(/\s+/);
                    if (parts.length < 4) continue;

                    // Vertex indekslerini parse et (OBJ 1-tabanlı, biz 0-tabanlı kullanıyoruz)
                    const faceIndices: number[] = [];
                    for (let i = 1; i < parts.length; i++) {
                        // "v", "v/vt", "v/vt/vn", "v//vn" formatlarını destekle
                        const vertexData = parts[i].split('/');
                        const vIdx = parseInt(vertexData[0]) - 1; // OBJ 1-tabanlı
                        if (!isNaN(vIdx) && vIdx >= 0) {
                            faceIndices.push(vIdx);
                        }
                    }

                    // Face'i üçgenlere dönüştür
                    if (faceIndices.length === 3) {
                        // Zaten üçgen
                        indices.push(faceIndices[0], faceIndices[1], faceIndices[2]);
                    } else if (faceIndices.length === 4) {
                        // Dörtgen - iki üçgene böl
                        indices.push(faceIndices[0], faceIndices[1], faceIndices[2]);
                        indices.push(faceIndices[0], faceIndices[2], faceIndices[3]);
                    } else if (faceIndices.length > 4) {
                        // Poligon - fan triangulation
                        for (let i = 1; i < faceIndices.length - 1; i++) {
                            indices.push(faceIndices[0], faceIndices[i], faceIndices[i + 1]);
                        }
                    }
                }
            }

            const vertexCount = positions.length / 3;
            const faceCount = indices.length / 3;

            // console.log(`[PointCloud] OBJ: ${vertexCount} vertex, ${faceCount} face yüklendi`);
            this.onProgress?.('Model oluşturuluyor...', 80);

            // Face varsa mesh olarak, yoksa point cloud olarak oluştur
            return this.createPointCloudGroup(
                dosya.name,
                positions,
                colors,
                indices.length > 0 ? indices : undefined
            );

        } catch (error: any) {
            console.error(`[PointCloud] OBJ import hatası:`, error);
            return null;
        }
    }

    /**
     * STL dosyası import et (ASCII ve Binary destekli)
     * STL her zaman mesh olarak yüklenir (sadece üçgenler içerir)
     */
    public async importSTL(dosya: File): Promise<FikoPointCloudGrup | null> {
        try {
            this.onProgress?.('STL dosyası okunuyor...', 20);

            const arrayBuffer = await dosya.arrayBuffer();
            const dataView = new DataView(arrayBuffer);

            // Binary mi ASCII mi kontrol et
            // Binary STL: İlk 80 byte header, sonra 4 byte triangle count
            // ASCII STL: "solid" ile başlar
            const header = new Uint8Array(arrayBuffer, 0, 5);
            const headerText = String.fromCharCode(...header);
            const isAscii = headerText.toLowerCase().startsWith('solid');

            let positions: number[] = [];
            let indices: number[] = [];
            let colors: number[] = [];

            if (isAscii) {
                // ASCII STL parse
                const text = new TextDecoder().decode(arrayBuffer);
                const lines = text.split('\n');

                let currentVertices: number[][] = [];

                for (const line of lines) {
                    const trimmed = line.trim().toLowerCase();

                    if (trimmed.startsWith('vertex ')) {
                        const parts = trimmed.split(/\s+/);
                        if (parts.length >= 4) {
                            const x = parseFloat(parts[1]);
                            const y = parseFloat(parts[2]);
                            const z = parseFloat(parts[3]);

                            if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
                                currentVertices.push([x, y, z]);

                                // Her 3 vertex'te bir üçgen oluştur
                                if (currentVertices.length === 3) {
                                    const baseIndex = positions.length / 3;

                                    for (const v of currentVertices) {
                                        positions.push(v[0], v[1], v[2]);
                                        colors.push(0.7, 0.7, 0.7); // Gri renk
                                    }

                                    indices.push(baseIndex, baseIndex + 1, baseIndex + 2);
                                    currentVertices = [];
                                }
                            }
                        }
                    }
                }

                // console.log(`[PointCloud] STL (ASCII): ${positions.length / 3} vertex, ${indices.length / 3} face`);

            } else {
                // Binary STL parse
                // Header: 80 bytes
                // Triangle count: 4 bytes (uint32)
                // Each triangle: 50 bytes (normal: 12, vertices: 36, attribute: 2)

                if (arrayBuffer.byteLength < 84) {
                    console.error('[PointCloud] STL dosyası çok kısa');
                    return null;
                }

                const triangleCount = dataView.getUint32(80, true); // little endian
                // console.log(`[PointCloud] STL (Binary): ${triangleCount} üçgen bekleniyor`);

                this.onProgress?.(`STL: ${triangleCount} üçgen okunuyor...`, 40);

                let offset = 84;
                for (let i = 0; i < triangleCount && offset + 50 <= arrayBuffer.byteLength; i++) {
                    // Normal (3 x float32 = 12 bytes) - atlıyoruz
                    offset += 12;

                    const baseIndex = positions.length / 3;

                    // 3 vertex (her biri 3 x float32 = 12 bytes)
                    for (let v = 0; v < 3; v++) {
                        const x = dataView.getFloat32(offset, true);
                        const y = dataView.getFloat32(offset + 4, true);
                        const z = dataView.getFloat32(offset + 8, true);
                        offset += 12;

                        positions.push(x, y, z);
                        colors.push(0.7, 0.7, 0.7); // Gri renk
                    }

                    // Attribute byte count (2 bytes) - atlıyoruz
                    offset += 2;

                    indices.push(baseIndex, baseIndex + 1, baseIndex + 2);
                }

                // console.log(`[PointCloud] STL (Binary): ${positions.length / 3} vertex, ${indices.length / 3} face yüklendi`);
            }

            if (positions.length === 0) {
                console.error('[PointCloud] STL dosyasında geçerli geometri bulunamadı');
                return null;
            }

            this.onProgress?.('Model oluşturuluyor...', 80);

            return this.createPointCloudGroup(
                dosya.name,
                positions,
                colors,
                indices
            );

        } catch (error: any) {
            console.error(`[PointCloud] STL import hatası:`, error);
            return null;
        }
    }

    /**
     * Position, color ve opsiyonel face array'lerinden grup oluştur
     * Face varsa mesh, yoksa point cloud oluşturur
     */
    private createPointCloudGroup(
        fileName: string,
        positions: number[],
        colors: number[],
        indices?: number[]
    ): FikoPointCloudGrup | null {
        if (positions.length === 0) {
            console.warn('[PointCloud] Dosyada geçerli nokta bulunamadı');
            return null;
        }

        const pointCount = positions.length / 3;
        const faceCount = indices ? indices.length / 3 : 0;
        const name = fileName.replace(/\.[^.]+$/, ''); // Uzantıyı kaldır

        // Grup oluştur
        const group = new FikoPointCloudGrup(this.sahne, name);

        // Geometry oluştur
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        // Face bilgisi varsa mesh, yoksa point cloud oluştur
        if (indices && indices.length > 0) {
            // MESH olarak yükle
            geometry.setIndex(indices);
            geometry.computeVertexNormals();
            geometry.computeBoundingBox();
            geometry.computeBoundingSphere();

            // Mesh material
            const material = new THREE.MeshPhongMaterial({
                vertexColors: true,
                side: THREE.DoubleSide,
                flatShading: false,
                shininess: 30
            });

            // Mesh oluştur
            const mesh = new THREE.Mesh(geometry, material);
            mesh.name = '__reconstructed_mesh__';
            group.add(mesh);

            // Point cloud verisini sakla (sonradan point cloud görünümüne geçiş için)
            group.pointCloudData = {
                positions: new Float32Array(positions),
                colors: new Float32Array(colors),
                normals: null,
                pointCount: pointCount,
                indices: new Uint32Array(indices),
                faceCount: faceCount
            };

            // Simgeyi mesh olarak güncelle (kupler.webp = 3D mesh simgesi)
            group.userData['FikoArayuz'].simge = '/simgeler/kupler.webp';

            this.onProgress?.(`✓ ${pointCount} vertex, ${faceCount} face yüklendi (MESH)`, 100);
            // console.log(`[PointCloud] ✓ MESH olarak yüklendi: ${pointCount} vertex, ${faceCount} face`);

        } else {
            // POINT CLOUD olarak yükle
            geometry.computeBoundingBox();
            geometry.computeBoundingSphere();

            // Points material
            const material = new THREE.PointsMaterial({
                size: 2,
                vertexColors: true,
                sizeAttenuation: true
            });

            // Points oluştur
            const points = new THREE.Points(geometry, material);
            points.name = '__point_cloud__';
            group.add(points);

            // Point cloud verisini sakla
            group.pointCloudData = {
                positions: new Float32Array(positions),
                colors: new Float32Array(colors),
                normals: null,
                pointCount: pointCount
            };

            this.onProgress?.(`✓ ${pointCount} nokta yüklendi (POINT CLOUD)`, 100);
            // console.log(`[PointCloud] ✓ POINT CLOUD olarak yüklendi: ${pointCount} nokta`);
        }

        return group;
    }
}
